import axios from 'axios';

// Configuración base de Axios (si tienes un archivo central, impórtalo)
// const apiClient = axios.create({ baseURL: '/api/tenant' });
// Por ahora, usaremos axios directamente asumiendo configuración global o proxy

const API_URL = '/api/tenant/products'; // Usamos la nueva ruta base

/**
 * Obtiene una lista paginada y filtrada de productos.
 * @param {object} params - Parámetros de consulta.
 * @param {number} [params.page]
 * @param {number} [params.itemsPerPage] - Se envía al API como pageSize.
 * @param {string} [params.q] - Búsqueda unificada por nombre, SKU (REFERENCE) o código de barras (CODE).
 * @param {string} [params.name] - Filtro legacy solo por nombre (preferir `q`).
 * @param {string} [params.code] - Filtro legacy solo por código (preferir `q`).
 * @param {string} [params.locationId]
 * @param {string} [params.categoryName]
 * @param {string[]} [params.sortBy]
 * @param {boolean[]} [params.sortDesc]
 * @returns {Promise<object>} - Promesa con los datos de la respuesta (products, totalCount, etc.)
 */
export const fetchProducts = async (params) => {
    try {
        // Construir parámetros, incluyendo ordenamiento
        const queryParams = { 
            page: params.page,
            pageSize: params.itemsPerPage || 10, 
            q: params.q,
            name: params.name,
            code: params.code,
            locationId: params.locationId,
            categoryName: params.categoryName,
            // Pasar parámetros de ordenamiento directamente
            // v-data-table los envía como arrays
            sortBy: params.sortBy, 
            sortDesc: params.sortDesc 
        };

        // Limpiar parámetros undefined o null (excepto page/pageSize que tienen defaults)
        Object.keys(queryParams).forEach(key => 
            (queryParams[key] === undefined || queryParams[key] === null || queryParams[key] === '') && 
            key !== 'page' && key !== 'pageSize' && 
            delete queryParams[key]
        );
        
        const response = await axios.get(API_URL, { 
            params: queryParams,
            // Importante para enviar arrays correctamente a Express/qs
            paramsSerializer: params => {
              const qs = require('qs');
              return qs.stringify(params, { arrayFormat: 'brackets' });
            } 
         });
        
        // Mapeo para asegurar la estructura y valores predeterminados
        const products = response.data.products.map(p => ({
            ...p,
            // Asegurar que price sea un número, defaulting a 0 si no lo es
            price: typeof p.price === 'number' ? p.price : 0,
            // Restaurar el mapeo seguro para category
            category: p.category || { ID: null, NAME: 'N/A' } 
        }));

        return { ...response.data, products }; // Devolver el objeto completo de la API con products mapeados
    } catch (error) {
        console.error('Error fetching products:', error);
        // Propagar el error para que el componente pueda manejarlo
        throw error;
    }
};

/**
 * Obtiene un producto por su ID.
 * @param {string} id - ID del producto.
 * @returns {Promise<object>} - Promesa con los datos del producto.
 */
export const getProductById = async (id) => {
    try {
        const response = await axios.get(`${API_URL}/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching product ${id}:`, error);
        throw error;
    }
};

/**
 * Crea un nuevo producto.
 * @param {object} productData - Datos del producto a crear.
 * @returns {Promise<object>} - Promesa con los datos del producto creado.
 */
export const createProduct = async (productData) => {
    try {
        const response = await axios.post(API_URL, productData);
        return response.data;
    } catch (error) {
        console.error('Error creating product:', error);
        throw error;
    }
};

/**
 * Actualiza un producto existente.
 * @param {string|number} productId - ID del producto a actualizar.
 * @param {object} productData - Nuevos datos del producto.
 * @returns {Promise<object>} - Promesa con los datos del producto actualizado.
 */
export const updateProduct = async (productId, productData) => {
    try {
        const response = await axios.put(`${API_URL}/${productId}`, productData);
        return response.data;
    } catch (error) {
        console.error(`Error updating product ${productId}:`, error);
        throw error;
    }
};

/**
 * Elimina un producto.
 * @param {string|number} productId - ID del producto a eliminar.
 * @returns {Promise<void>} - Promesa vacía si la eliminación es exitosa.
 */
export const deleteProduct = async (productId) => {
    try {
        await axios.delete(`${API_URL}/${productId}`);
    } catch (error) {
        console.error(`Error deleting product ${productId}:`, error);
        throw error;
    }
};

/**
 * Descarga la plantilla de Excel para carga masiva.
 * @returns {Promise<Blob>} - Blob del archivo Excel.
 */
export const downloadTemplate = async () => {
    try {
        const response = await axios.get(`${API_URL}/template`, {
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        console.error('Error downloading template:', error);
        throw error;
    }
};

/**
 * Descarga el reporte de productos de venta en Excel.
 * @returns {Promise<Blob>} - Blob del archivo Excel.
 */
export const downloadSalesReport = async () => {
    try {
        const response = await axios.get(`${API_URL}/report/sales`, {
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        console.error('Error downloading sales products report:', error);
        throw error;
    }
};

/**
 * Obtiene la vista previa del reporte de productos de venta.
 * @param {object} params - Filtros del reporte.
 * @returns {Promise<object>} - Resultado con filas y metadata.
 */
export const fetchSalesReportData = async (params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/report/sales/data`, {
            params
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching sales products report data:', error);
        throw error;
    }
};

/**
 * Descarga el reporte de productos de venta en Excel con filtros.
 * @param {object} params - Filtros del reporte.
 * @returns {Promise<Blob>} - Blob del archivo Excel.
 */
export const downloadSalesReportWithFilters = async (params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/report/sales`, {
            params,
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        console.error('Error downloading sales products report:', error);
        throw error;
    }
};

/**
 * Sube un archivo Excel para carga masiva de productos.
 * @param {File} file - Archivo Excel.
 * @returns {Promise<object>} - Resultado de la importación.
 */
export const bulkImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
        const response = await axios.post(`${API_URL}/bulk-import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading bulk products:', error);
        throw error;
    }
};
